import "./App.css";
import ProgressBar from "./Components/ProgressBar/ProgressBar";
import {
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
  CheckIcon,
} from "@heroicons/react/solid";
import PetAge from "./Components/PetInfoSteps/PetAge";
import { useState, useEffect, useRef, useCallback } from "react";
import PetBodyInfo from "./Components/PetInfoSteps/PetBodyInfo";
import Alert from "./Components/FormAlert/Alert";
import PetAlergic from "./Components/PetInfoSteps/PetAlergic";
import UserInfo from "./Components/PetInfoSteps/UserInfo";
import Recommendations from "./Components/PetInfoSteps/Recommendations";
import { calculateService } from "./Services/calculate.service";
import { emailService } from "./Services/email.service";
import { leftDog, rightDog, buttonDog, rightCat, leftCat, buttonCat } from "./assets/svg";
import { useTranslation } from "react-i18next";
import { useParams } from 'react-router-dom';

/**
 * Step Flow:
 *   1 = Profil    (PetAge + pet name, lifeStage, breedSize, isNeutral)
 *   2 = Fizik     (PetBodyInfo: weight, activity, bodyType)
 *   3 = Sağlık    (PetAlergic: allergies, health — optional)
 *   4 = İletişim  (UserInfo: email/phone — hidden from progress bar)
 *   5 = Öneriler  (Recommendations: nutrition calculation results)
 *   6 = Tamamlanma (Summary + close)
 *
 * Progress bar shows: Profil → Fizik → Sağlık → Sonuç
 * "İletişim" is invisible on the bar — from the user's perspective they go
 * from Sağlık straight to "Sonuç", reducing perceived friction.
 */
function App() {
  const [step, setStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [alert, setAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Null");
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // null = never sent; object = snapshot of last sent state
  const [lastSentSnapshot, setLastSentSnapshot] = useState(null);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [isStepLoading, setIsStepLoading] = useState(false);

  const { t } = useTranslation();
  const { petType } = useParams();

  const [petInfo, setPetInfo] = useState({
    id: "",
    step: "",
    name: "",
    breed: "",
    gender: "",
    isNeutral: null,
    kg: "",
    activity: "",
    bodyType: "",
    hasAlergic: "",
    allergies: "",
    disease: "",
    hasHealthIssues: "",
    petType: petType,
    lifeStage: "",
    ageMonths: null,
    breedSize: "",
    userInfo: {
      name: "",
      surname: "",
      email: "",
      phone: "",
    },
  });

  const [preferredBundle, setPreferredBundle] = useState(null);
  const [maxStep, setMaxStep] = useState(1);
  const [abandonmentSent, setAbandonmentSent] = useState(false);

  // ---------------------------------------------------------------------------
  // Form abandonment detection
  // ---------------------------------------------------------------------------
  const handleClose = useCallback(async () => {
    // If form is not completed (step < 6) and we have a pet ID, notify admin
    if (step < 6 && petInfo.id && !abandonmentSent && !lastSentSnapshot) {
      setAbandonmentSent(true);
      // Fire-and-forget: don't block closing
      emailService.sendFormAbandoned(petInfo.id, step).catch(() => { });
    }
    window.parent.postMessage({ action: "kito_close_modal" }, "*");
  }, [step, petInfo.id, abandonmentSent, lastSentSnapshot]);

  // Also detect browser tab close / navigation away
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (step < 6 && petInfo.id && !abandonmentSent && !lastSentSnapshot) {
        // Use sendBeacon for reliable delivery on page unload
        const apiBase = process.env.NODE_ENV === "development"
          ? "http://localhost:3000/"
          : "https://vd7p0j8os9.execute-api.eu-central-1.amazonaws.com/prod/";
        const url = `${apiBase}user/form-abandoned`;
        const data = JSON.stringify({ petId: petInfo.id, step: step });
        navigator.sendBeacon(url, new Blob([data], { type: "application/json" }));
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [step, petInfo.id, abandonmentSent, lastSentSnapshot]);

  // ---------------------------------------------------------------------------
  // Progress bar generation
  // ---------------------------------------------------------------------------
  // "İletişim" is included so the ProgressBar can filter it out automatically.
  // Each item carries its real `stepNum` so clicking a completed dot navigates
  // to the correct internal step — even when hidden steps shift the indices.
  const barSteps = [
    { name: t('stepProfile'), stepNum: 1 },
    { name: t('stepPhysical'), stepNum: 2 },
    { name: t('stepHealth'), stepNum: 3 },
    { name: t('stepContact'), stepNum: 4 },
    { name: t('stepResult'), stepNum: 5 },
  ].map((item, index) => {
    const stepNum = item.stepNum;
    // "Sonuç" (stepNum 5) represents steps 4-6 on the bar
    if (stepNum === 5) {
      if (step === 6) return { ...item, status: 'complete' };
      if (step >= 4) return { ...item, status: 'current' };
      if (maxStep >= 4) return { ...item, status: 'complete' };
      return { ...item, status: 'upcoming' };
    }

    if (stepNum === step) return { ...item, status: 'current' };
    if (stepNum < step) return { ...item, status: 'complete' };
    if (stepNum <= maxStep) return { ...item, status: 'complete' };
    return { ...item, status: 'upcoming' };
  });

  // ---------------------------------------------------------------------------
  // Validation & step progression
  // ---------------------------------------------------------------------------
  const completeStep = async () => {
    setIsStepLoading(true);
    let formIsComplete = false;
    let missingFieldMessage = "";

    if (step === 1) {
      // Profil: lifeStage + (breedSize for dogs) + isNeutral
      if (!petInfo.lifeStage) {
        missingFieldMessage = t("validationSelectLifeStage") || "Lütfen yaşam evresini seçin";
      } else if (petType === "dog" && !petInfo.breedSize) {
        missingFieldMessage = t("validationSelectBreedSize") || "Lütfen ırk boyutunu seçin";
      } else if (petInfo.isNeutral === null || petInfo.isNeutral === undefined) {
        missingFieldMessage = t("validationSelectNeutered") || "Lütfen kısırlaştırma durumunu seçin";
      } else {
        formIsComplete = true;
      }
    } else if (step === 2) {
      // Fizik
      if (!petInfo.kg || Number(petInfo.kg) === 0) {
        missingFieldMessage = t("validationEnterWeight") || "Lütfen kiloyu girin";
      } else if (!petInfo.activity) {
        missingFieldMessage = t("validationSelectActivity") || "Lütfen aktivite seviyesini seçin";
      } else if (!petInfo.bodyType) {
        missingFieldMessage = t("validationSelectBodyType") || "Lütfen vücut tipini seçin";
      } else {
        formIsComplete = true;
      }
    } else if (step === 3) {
      // Sağlık — optional, always pass
      formIsComplete = true;
    } else if (step === 4) {
      // İletişim — email zorunlu, telefon isteğe bağlı
      const isEmailValid = petInfo.userInfo?.email && petInfo.userInfo.email.includes("@");
      const privacyAccepted = petInfo.userInfo?.privacyPolicyAccepted;

      if (!isEmailValid) {
        missingFieldMessage = t("validationEnterEmail") || "Lütfen geçerli bir e-posta adresi girin";
      } else if (!privacyAccepted) {
        missingFieldMessage = t("validationAcceptPrivacy") || "Lütfen gizlilik politikasını kabul edin";
      } else {
        formIsComplete = true;
      }
    } else {
      formIsComplete = true;
    }

    if (formIsComplete) {
      // Force a render with isStepLoading=true before the async save
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      await savePetData().catch(() => { });
      setIsStepLoading(false);
      setMaxStep(Math.max(maxStep, step + 1));
      setStep(step + 1);
    } else {
      setIsStepLoading(false);
      setErrorMessage(missingFieldMessage || t("fillRequiredFields") || "Lütfen zorunlu alanları doldurun");
      setAlert(true);
      setTimeout(() => {
        setAlert(false);
        setErrorMessage("Null");
      }, 3000);
    }
  };

  const previousStep = () => {
    if (step > 1) setStep(step - 1);
  };

  // ---------------------------------------------------------------------------
  // Save pet data to DB via /food/create  (payload built from petInfo directly)
  // ---------------------------------------------------------------------------
  const savePetData = async () => {
    try {
      const payload = {
        id: petInfo.id || "",
        animalName: petInfo.name || "",
        animalGenus: petInfo.breed || "",
        gender: petInfo.gender || "",
        isBarren: petInfo.isNeutral === true,
        dateOfBirth: new Date().toISOString(),
        weight: petInfo.kg ? Math.max(2, Math.min(46, Number(petInfo.kg))) : 0,
        isAllergy: petInfo.hasAlergic === true,
        allergies: petInfo.allergies || "",
        isDisease: petInfo.hasHealthIssues === true,
        disease: petInfo.disease || "",
        activity: petInfo.activity || "",
        bodyType: petInfo.bodyType || "",
        foodType: [],
        isSteps: step,
        petType: petType,
        userInfo: petInfo.userInfo || null,
        lifeStage: petInfo.lifeStage || "adult",
        ageMonths: petInfo.ageMonths || 12,
        breedSize: petInfo.breedSize || "medium",
        name: petInfo.userInfo?.name || "",
        email: petInfo.userInfo?.email || "",
        phone: petInfo.userInfo?.phone || "",
      };

      const res = await calculateService.calculeteData(payload);
      if (res?.data?.id) {
        const newId = res.data.id;
        if (!petInfo.id) {
          setPetInfo((prev) => ({ ...prev, id: newId }));
        }
        return newId;
      }
    } catch (e) {
      console.error("Error saving pet data:", e);
    }
  };

  // ---------------------------------------------------------------------------
  // Save data + send professional report + proceed to completion
  // ---------------------------------------------------------------------------
  const sendPetInfo = async () => {
    const savedPetId = await savePetData();
    const petIdToUse = savedPetId || petInfo.id;

    if (!petIdToUse) {
      console.error("Cannot send report: no pet ID available");
      setErrorMessage(t("errorSavingPet") || "Pet bilgileri kaydedilemedi, lütfen tekrar deneyin.");
      setAlert(true);
      setTimeout(() => setAlert(false), 3000);
      return;
    }

    // Build a snapshot of the current state that affects the report content
    const currentSnapshot = {
      bundleId: preferredBundle?.id ?? null,
      weight: petInfo.kg,
      activity: petInfo.activity,
      bodyType: petInfo.bodyType,
      lifeStage: petInfo.lifeStage,
      allergies: petInfo.allergies,
    };

    const isDuplicate = lastSentSnapshot &&
      JSON.stringify(currentSnapshot) === JSON.stringify(lastSentSnapshot);

    if (!isDuplicate) {
      setIsSendingReport(true);
      try {
        const result = await emailService.sendProfessionalReport(petIdToUse, preferredBundle?.id, petInfo.userInfo?.contactPermission ?? false);
        if (!result.success) throw new Error("Report send failed");
        setLastSentSnapshot(currentSnapshot);
      } catch (error) {
        console.error("Error sending report:", error);
        setErrorMessage(t("errorSendingReport") || "Rapor gönderilirken bir hata oluştu");
        setAlert(true);
        setTimeout(() => setAlert(false), 3000);
        setIsSendingReport(false);
        return;
      } finally {
        setIsSendingReport(false);
      }
    }

    // Move to completion
    completeStep();
  };

  // ---------------------------------------------------------------------------
  // Scroll to top on step change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setPetInfo((prev) => ({ ...prev, step }));
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
  }, [step]);

  // ---------------------------------------------------------------------------
  // Scroll indicator: show pulsing arrow when content overflows
  // ---------------------------------------------------------------------------
  const checkScrollable = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const hasMore = el.scrollHeight - el.scrollTop - el.clientHeight > 30;
    setShowScrollHint(hasMore);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    // Check on mount and after layout settles
    const timer = setTimeout(checkScrollable, 200);
    el.addEventListener("scroll", checkScrollable, { passive: true });
    // Re-check on resize
    window.addEventListener("resize", checkScrollable);
    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", checkScrollable);
      window.removeEventListener("resize", checkScrollable);
    };
  }, [step, checkScrollable]);

  // ---------------------------------------------------------------------------
  // Footer visibility helpers
  // ---------------------------------------------------------------------------
  const showFooter = step <= 5;
  const showBackButton = step > 1 && step <= 5;
  const isReportStep = step === 5;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="App kito-app-container relative flex flex-col h-[100dvh] w-full bg-[#faf6ee] overflow-hidden md:px-6 xl:px-8">
      {/* Floating Alert — outside of footer so it's always visible */}
      <Alert alert={alert} setAlert={setAlert} message={errorMessage} />

      {/* Floating Card Wrapper */}
      <div className="flex-1 w-full max-w-6xl mx-auto md:my-8 bg-white md:rounded-[2rem] md:shadow-xl overflow-hidden flex flex-col relative">
        {/* Header: Progress Bar + Close Button */}
        <div className="flex-shrink-0 z-20 relative pt-0">
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white/95 backdrop-blur-sm border-2 border-[#8B5C47]/30 rounded-full text-[#8B5C47] shadow-sm hover:scale-105 hover:bg-white hover:border-[#8B5C47] transition-all duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="sm:w-[18px] sm:h-[18px]">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <ProgressBar
            barSteps={barSteps}
            step={step}
            setStep={setStep}
            setIsComplete={setIsComplete}
            isComplete={isComplete}
            petType={petType}
          />
        </div>

        {/* Scrollable Content */}
        <div
          className="kito-content flex-1 overflow-y-auto w-full relative overscroll-contain flex flex-col"
          ref={scrollContainerRef}
        >
          {/* Report Loading Overlay */}
          {isSendingReport && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
              <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-xl">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-600 border-t-transparent mb-4"></div>
                <p className="text-gray-700 font-medium">{t("sendingReport") || "Raporunuz hazırlanıyor..."}</p>
              </div>
            </div>
          )}

          <div className="flex-1 w-full flex justify-around items-start py-4 min-h-fit">
            {/* Left Decoration (desktop) */}
            <img
              className="hidden h-8 w-36 self-start mt-16 md:flex md:h-32 lg:h-36"
              src={petType === "dog" ? leftDog : leftCat}
              alt="decoration"
            />

            {/* Step Content */}
            <div className="flex-1 flex justify-center items-start pt-4 sm:pt-8 py-8">
              {step === 1 && (
                <PetAge setPetInfo={setPetInfo} petInfo={petInfo} setIsComplete={setIsComplete} petType={petType} />
              )}
              {step === 2 && (
                <PetBodyInfo setPetInfo={setPetInfo} petInfo={petInfo} setIsComplete={setIsComplete} petType={petType} />
              )}
              {step === 3 && (
                <PetAlergic setPetInfo={setPetInfo} petInfo={petInfo} setIsComplete={setIsComplete} petType={petType} />
              )}
              {step === 4 && (
                <UserInfo setPetInfo={setPetInfo} petInfo={petInfo} setIsComplete={setIsComplete} petType={petType} />
              )}
              {step === 5 && (
                <Recommendations
                  petInfo={petInfo}
                  setPetInfo={setPetInfo}
                  setPreferredBundle={setPreferredBundle}
                  setStep={setStep}
                  petType={petType}
                  petAllergies={petInfo.allergies || ""}
                />
              )}
              {step === 6 && (
                <div className="flex flex-col items-center justify-center text-center p-6 animate-fade-in-up w-full max-w-lg mx-auto">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 shadow-sm">
                    <CheckIcon className="w-10 h-10 text-kito-green" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2 font-poppins">
                    {t("reportReady") || "Raporunuz Hazır!"} 📩
                  </h2>
                  <p className="text-gray-500 text-sm mb-6 font-poppins">
                    {t("reportSentTo")}{" "}
                    <strong>{petInfo.userInfo?.email}</strong>{" "}
                    {t("reportSentToSuffix")}
                  </p>

                  {/* Monthly Supply Summary */}
                  {preferredBundle && (
                    <div className="w-full bg-white border-2 border-orange-100 rounded-2xl p-4 mb-6 shadow-sm text-left relative overflow-hidden">
                      <div className="absolute top-0 right-0 bg-orange-100 px-3 py-1 rounded-bl-xl text-[10px] font-bold text-orange-600 uppercase tracking-wide">
                        {t("monthlyNeed") || "Aylık İhtiyaç"}
                      </div>
                      <h3 className="font-bold text-gray-800 text-lg mb-1">
                        {t(`bundle_${preferredBundle.id?.replace(/-/g, '_')}_name`) || preferredBundle.name}
                      </h3>
                      <p className="text-xs text-gray-400 mb-3">
                        {t(`bundle_${preferredBundle.id?.replace(/-/g, '_')}_desc`) || preferredBundle.description}
                      </p>

                      <div className="space-y-2">
                        {preferredBundle.products?.map((prod, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-orange-50/50 p-2 rounded-lg border border-orange-100">
                            <div className="flex items-center flex-1 mr-2">
                              <span className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${prod.productType === "fresh" ? "bg-green-500" : "bg-yellow-500"}`} />
                              <span className="text-sm font-medium text-gray-700 text-left">
                                {prod.productType === 'dry' ? t('kitoDryLabel') : prod.productName}
                              </span>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="font-bold text-orange-600 text-sm">
                                {prod.productType === "fresh"
                                  ? `${prod.monthlyPackages || 1} ${t('packet')}`
                                  : `~${((prod.dailyGrams * 30) / 1000).toFixed(1)} kg`}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {((prod.dailyGrams * 30) / 1000).toFixed(1)} {t('kgPerMonth')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Info Note */}
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 w-full mb-6">
                    <p className="text-blue-800 text-xs font-medium">
                      🐾 {t("reportDetailNote") || "Raporunuzda daha detaylı içerik ve analizleri bulabilirsiniz."}
                    </p>
                  </div>

                  <a
                    href="https://kito.pet/collections/urunler-2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full block text-center py-3 bg-gray-800 text-white rounded-xl font-bold shadow-lg hover:bg-gray-700 transform hover:scale-[1.02] transition-all duration-200"
                  >
                    {t("goToProducts") || "Ürünlere Git"}
                  </a>
                </div>
              )}
            </div>

            {/* Right Decoration (desktop) */}
            <img
              className="hidden h-8 w-36 self-end mb-16 md:flex md:h-32 lg:h-[150px]"
              src={petType === "dog" ? rightDog : rightCat}
              alt="decoration"
            />
          </div>
        </div>

        {/* Scroll Hint Arrow */}
        {showScrollHint && showFooter && (
          <button
            onClick={() => {
              const el = scrollContainerRef.current;
              if (el) el.scrollBy({ top: 150, behavior: "smooth" });
            }}
            className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 animate-bounce bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center shadow-lg transition-opacity duration-300"
            aria-label="Scroll down"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}

        {/* Footer: Navigation Buttons */}
        {showFooter && (
          <div className="kito-footer flex-shrink-0 z-30 w-full bg-white">
            <div className="h-20 flex items-center justify-center pb-[env(safe-area-inset-bottom)]">
              {showBackButton && (
                <button
                  onClick={previousStep}
                  type="button"
                  className="mr-3 sm:mr-4 inline-flex items-center px-3 sm:px-6 py-2 sm:py-3 border border-gray-300 shadow-sm text-sm sm:text-base font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-kito-green whitespace-nowrap"
                >
                  <ChevronDoubleLeftIcon className="sm:mr-2 mr-0 -ml-1 h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  <span className="hidden sm:inline">{t("previousStep") || "Geri"}</span>
                </button>
              )}

              <div className="flex items-center">
                <img className="h-10 mr-3 sm:mr-5 sm:h-12" src={petType === "dog" ? buttonDog : buttonCat} alt="mascot" />
                <button
                  onClick={isReportStep ? sendPetInfo : completeStep}
                  type="button"
                  disabled={isStepLoading}
                  className={`inline-flex items-center px-6 sm:px-8 py-2 sm:py-3 border border-transparent shadow-sm text-sm sm:text-base font-medium rounded-full text-white bg-kito-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-kito-green whitespace-nowrap ${isStepLoading ? 'opacity-70' : ''}`}
                >
                  {isReportStep ? (t("sendMyReport") || "Raporumu Gönder") : (t("nextStep") || "İleri")}
                  {isReportStep && !isStepLoading ? (
                    <CheckIcon className="ml-2 -mr-1 h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  ) : isStepLoading ? (
                    <div className="ml-2 -mr-1 animate-spin h-5 w-5 sm:h-6 sm:w-6 border-2 border-white/30 border-t-white rounded-full"></div>
                  ) : (
                    <ChevronDoubleRightIcon className="ml-2 -mr-1 h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
